import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type {
  VoiceCallEditorItem,
  OauthAccountOption,
} from './VoiceCallCardStack';
import { ReactComponent as CloseXIcon } from '../../icons/close-x.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';

/** Props mirror VoiceCallTodoCard for visual consistency. */
export interface VoiceCallEditorCardProps {
  card: VoiceCallEditorItem;
  oauthAccounts?: OauthAccountOption[];
  onContentChange: (id: string, content: string) => void;
  onFieldChange: (
    id: string,
    field: 'title' | 'to' | 'cc' | 'bcc' | 'subject' | 'oauthId',
    value: string | number,
  ) => void;
  onRemove: (id: string) => void;
  onSaveDocument: (id: string) => void;
  onSaveEmailDraft: (id: string) => void;
}

type EditorTab = 'edit' | 'preview';

/**
 * Inline editor card rendered in the VoiceCallCardStack during a voice call.
 *
 * Layout:
 *  - Title input (no uppercase type header — the card chrome makes type obvious).
 *  - Email-only recipient strip (From / To / Subject / Cc / Bcc).
 *  - For documents: Edit/Preview tab bar above the body so the user can review
 *    rendered markdown while the agent streams in patches.
 *  - Body — markdown textarea (Edit) or react-markdown (Preview).
 *  - Footer with error banner and save button.
 *
 * Sizing:
 *  - Email drafts stay compact; the textarea auto-grows up to 260px.
 *  - Documents fill the expanded card on desktop (sized in SCSS) so there's
 *    room to actually read/write prose without a cramped little box.
 */
const VoiceCallEditorCard: React.FC<VoiceCallEditorCardProps> = ({
  card,
  oauthAccounts,
  onContentChange,
  onFieldChange,
  onRemove,
  onSaveDocument,
  onSaveEmailDraft,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isEmail = card.editorType === 'email_draft';
  const isDocument = card.editorType === 'document';
  const isSaved = card.status === 'saved';
  const isSaving = card.status === 'saving';
  const isError = card.status === 'error';
  const isLocked = isSaved || isSaving;

  // Documents get an Edit/Preview toggle. Email drafts only ever show the
  // textarea (no preview needed — the user knows what their email looks like).
  const [activeTab, setActiveTab] = useState<EditorTab>('edit');
  // If the card becomes locked (saved) flip to preview automatically for docs —
  // nothing to edit anymore, but the user may want to re-read the rendered copy.
  useEffect(() => {
    if (isDocument && isLocked) setActiveTab('preview');
  }, [isDocument, isLocked]);

  // Email textarea auto-grow. Documents use CSS flex sizing so we skip it there.
  // We read the cap from the element's computed max-height instead of a JS
  // constant so the cap automatically tracks the SCSS (which differs between
  // mobile and desktop email variants).
  useEffect(() => {
    if (!isEmail) return;
    if (activeTab !== 'edit') return;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = '0px';
    const computedMax = parseFloat(getComputedStyle(ta).maxHeight);
    const cap =
      Number.isFinite(computedMax) && computedMax > 0 ? computedMax : 260;
    const next = Math.min(ta.scrollHeight, cap);
    ta.style.height = `${next}px`;
  }, [card.content, isEmail, activeTab]);

  const handleSave = useCallback(() => {
    if (isLocked) return;
    if (isEmail) onSaveEmailDraft(card.id);
    else onSaveDocument(card.id);
  }, [isLocked, isEmail, onSaveDocument, onSaveEmailDraft, card.id]);

  const saveLabel = isEmail ? 'Save as Draft' : 'Save Document';
  const savedLabel = isEmail ? 'Draft saved' : 'Document saved';

  const cardClassName = [
    'voice-call-editor-card',
    isDocument
      ? 'voice-call-editor-card--document'
      : 'voice-call-editor-card--email',
    isSaved ? 'saved' : '',
    isError ? 'errored' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClassName}>
      {/* Remove button (only while actively editing) */}
      {!isLocked && (
        <button
          className='voice-editor-remove-btn'
          onClick={() => onRemove(card.id)}
          title='Remove'
        >
          <CloseXIcon />
        </button>
      )}

      {/* Title input — acts as the card's primary heading */}
      <input
        type='text'
        className='voice-editor-title-input'
        value={card.title}
        placeholder={isEmail ? 'Subject / title…' : 'Document title…'}
        onChange={(e) => onFieldChange(card.id, 'title', e.target.value)}
        disabled={isLocked}
      />

      {/* Email-only sub-header (recipients + account picker) */}
      {isEmail && (
        <div className='voice-editor-email-fields'>
          <label className='voice-editor-field-row'>
            <span className='voice-editor-field-label'>From</span>
            <select
              className='voice-editor-field-input'
              value={card.oauthId ?? ''}
              onChange={(e) =>
                onFieldChange(card.id, 'oauthId', Number(e.target.value))
              }
              disabled={isLocked}
            >
              <option value='' disabled>
                Select account…
              </option>
              {oauthAccounts?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.email}
                </option>
              ))}
            </select>
          </label>
          <label className='voice-editor-field-row'>
            <span className='voice-editor-field-label'>To</span>
            <input
              className='voice-editor-field-input'
              value={card.to ?? ''}
              placeholder='recipient@example.com'
              onChange={(e) => onFieldChange(card.id, 'to', e.target.value)}
              disabled={isLocked}
            />
          </label>
          <label className='voice-editor-field-row'>
            <span className='voice-editor-field-label'>Subject</span>
            <input
              className='voice-editor-field-input'
              value={card.subject ?? ''}
              placeholder='Subject line'
              onChange={(e) =>
                onFieldChange(card.id, 'subject', e.target.value)
              }
              disabled={isLocked}
            />
          </label>
          <div className='voice-editor-field-row voice-editor-cc-row'>
            <label className='voice-editor-cc-field'>
              <span className='voice-editor-field-label'>Cc</span>
              <input
                className='voice-editor-field-input'
                value={card.cc ?? ''}
                onChange={(e) => onFieldChange(card.id, 'cc', e.target.value)}
                disabled={isLocked}
              />
            </label>
            <label className='voice-editor-cc-field'>
              <span className='voice-editor-field-label'>Bcc</span>
              <input
                className='voice-editor-field-input'
                value={card.bcc ?? ''}
                onChange={(e) => onFieldChange(card.id, 'bcc', e.target.value)}
                disabled={isLocked}
              />
            </label>
          </div>
        </div>
      )}

      {/* Edit/Preview tabs — documents only */}
      {isDocument && (
        <div
          className='voice-editor-tabs'
          role='tablist'
          aria-label='Editor mode'
        >
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'edit'}
            className={`voice-editor-tab${activeTab === 'edit' ? ' active' : ''}`}
            onClick={() => setActiveTab('edit')}
            disabled={isLocked}
          >
            Edit
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'preview'}
            className={`voice-editor-tab${activeTab === 'preview' ? ' active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            Preview
          </button>
        </div>
      )}

      {/* Body */}
      {isDocument && activeTab === 'preview' ? (
        <div className='voice-editor-preview' role='tabpanel'>
          {card.content.trim() ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              /* Render links in a new tab — preview should never navigate the app away. */
              components={{
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                a: ({ node, ...props }) => (
                  <a {...props} target='_blank' rel='noopener noreferrer' />
                ),
              }}
            >
              {card.content}
            </ReactMarkdown>
          ) : (
            <div className='voice-editor-preview-empty'>
              Nothing to preview yet — switch to Edit and start typing, or let
              the assistant dictate.
            </div>
          )}
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          className='voice-editor-body'
          value={card.content}
          placeholder='Start typing or let the assistant dictate…'
          onChange={(e) => onContentChange(card.id, e.target.value)}
          disabled={isLocked}
          spellCheck
        />
      )}

      {/* Error banner */}
      {isError && card.saveError && (
        <div className='voice-editor-error'>{card.saveError}</div>
      )}

      {/* Footer — save action */}
      <div className='voice-editor-footer'>
        {isSaved ? (
          <span className='voice-editor-saved-badge'>
            <CheckmarkIcon /> {savedLabel}
          </span>
        ) : (
          <button
            className='voice-editor-save-btn'
            onClick={handleSave}
            disabled={isSaving || (isEmail && !card.oauthId)}
          >
            {isSaving ? 'Saving…' : saveLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default VoiceCallEditorCard;
